import { Box, Grid2 } from '@mui/material';
import { CustomChip, CustomTypography } from '@qler-view/shared_lib/src';
import SessionIcon from '@assets/images/sessionIcon.svg';
import CustomButton from '@ui-components/Button/CustomButton';
import './facility.css';
import { useState, useEffect, useCallback } from 'react';
import waitingBgImage from '@assets/images/waiting_bg.png';
import {
  useUpdateConsultStatus,
  useWaitingRoomDetails,
  useCallHeartBeat,
  usePutConsultOnHold,
} from '../../API/hooks/waitingHook';
import { DeleteHeartBeatAPI } from '../../API/services/waitingService';
import { useSearchParams, useNavigate } from 'react-router-dom';
import type { CustomChipColor } from '@qler-view/shared_lib/src/shared/lib/ui-components/Chip/CustomChip';
import {
  getChipDetails,
  callStatusToString,
  CallStatusEnum,
} from '../components/ConsultStatusChip';

import ConfirmationPopup from '../components/ConfirmationPopup';
import CustomSnackbar from '@ui-components/Snackbar/CustomSnackbar';
import {
  stopAlert,
  warmUpAudio,
  initAudio,
  isAudioUnlocked,
} from '../../shared/lib/utils/audioManager';
import AlertSound from '@assets/ringtone.mp3';
import { useStartZoomMeeting } from '../../shared/zoom/zoomSetup';
import MonitorIcon from '@assets/images/MonitorIcon.svg';
import { getUserFromStorage } from '../components/storageUtility';
import {
  FACILITY_CONSULT_REQUEST_DASHBOARD_ROUTE_PATH,
  CASEMANGER_CONSULT_REQUEST_DASHBOARD_ROUTE_PATH,
  RE_SUBMIT_OR_CANCEL_CONSULT_ROUTE_PATH,
} from '../../routers/RoutPath';
import { ROLES } from '../../roles';
import ChatQLER from '@qler-view/shared_lib/src/shared/Chat/ChatQLER';
import {
  useGetAcsChatToken,
  useGetConsultChatThread,
} from '../../API/hooks/acsChatHook';
import { PORTAL_TYPES } from '@qler-view/shared_lib/src/shared/constants/portalType';
import CustomBadge from '@qler-view/shared_lib/src/shared/lib/ui-components/Badge/CustomBadge';
import { getMaskedOrOriginal } from '../components/phiMasking';
import TimeTicker from '@qler-view/shared_lib/src/shared/reusableComponents/TimeTicker';
import TimeDifference from '@qler-view/shared_lib/src/shared/reusableComponents/TimeDifference';
import CancelConsultModel, {
  type CancelReasonFacilitySubmitParams,
  type CancelReasonFacilityParams,
} from '@qler-view/shared_lib/src/shared/statusrequests/CancelConsultModel';
import OnHoldConsultModel, {
  type OnHoldReasonFacilityParams,
} from '@qler-view/shared_lib/src/shared/statusrequests/OnHoldConsultModel';
import {
  useCancelConsultMetadata,
  useSaveCancelConsult,
  useOnHoldConsultMetadata,
  useSaveOnHoldConsult,
} from '../../shared/statusRequest/StatusRequestSetup';

const Waiting = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const consultId = searchParams.get('consultId');
  const user = getUserFromStorage();
  const [openCancelRequest, setOpenCancelRequest] = useState(false);
  const [openOnHoldConsult, setOpenOnHoldConsult] = useState(false);
  const [openConfirmationPopup, setOpenConfirmationPopup] = useState(false);
  const [showCallCompletedToast, setShowCallCompletedToast] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showAudioOverlay, setShowAudioOverlay] = useState(false);
  const [hasJoinedCall, setHasJoinedCall] = useState(false);
  const [onHoldConsultValues, setOnHoldConsultValues] =
    useState<OnHoldReasonFacilityParams>({} as OnHoldReasonFacilityParams);
  const { waitingRoomData, isLoading } = useWaitingRoomDetails(
    Number(consultId),
    user?.userId ?? 0
  );

  const { consultStatus, isLoading: isStatusLoading } = useUpdateConsultStatus(
    Number(consultId) || 0,
    user?.userId ?? 0
  );

  const { startZoomMeeting } = useStartZoomMeeting(PORTAL_TYPES.FACILITY);

  const currentStatus = consultStatus?.status ?? waitingRoomData?.status;

  const isWaitingStatus =
    currentStatus?.toLowerCase() === CallStatusEnum.OnHold.toLowerCase() ||
    currentStatus?.toLowerCase() === CallStatusEnum.New.toLowerCase() ||
    currentStatus?.toLowerCase() === CallStatusEnum.InProgress.toLowerCase() ||
    currentStatus?.toLowerCase() === CallStatusEnum.CallStarted.toLowerCase();

  useCallHeartBeat(
    Number(consultId) || 0,
    PORTAL_TYPES.FACILITY,
    user?.userId ?? 0,
    isWaitingStatus && !hasJoinedCall
  );

  const { color: chipColor, icon: chipIcon } = getChipDetails(
    currentStatus?.toLocaleLowerCase() as CallStatusEnum
  );

  const [isChatOpen, setIsChatOpen] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const { CancelConsultMetadata: ConsultCancelMetaData } =
    useCancelConsultMetadata(PORTAL_TYPES.FACILITY);
  const { mutate: cancelConsult } = useSaveCancelConsult();

  const { OnHoldConsultMetadata } = useOnHoldConsultMetadata(
    PORTAL_TYPES.FACILITY
  );
  const { mutate: saveOnHoldConsult } = useSaveOnHoldConsult();
  const { mutate: putConsultOnHold } = usePutConsultOnHold();

  const { acsChatToken } = useGetAcsChatToken(
    user?.userId ?? 0,
    PORTAL_TYPES.FACILITY
  );
  const { consultChatThread } = useGetConsultChatThread(
    consultId ? Number(consultId) : 0,
    user?.userId ?? 0,
    PORTAL_TYPES.FACILITY,
    isChatOpen
  );

  const isPHIMaskingEnabled = waitingRoomData?.isHidePhiAfterCompletion;

  useEffect(() => {
    const img = new Image();
    img.src = waitingBgImage;
    img.onload = () => setImageLoaded(true);
    img.onerror = () => setImageLoaded(true);
  }, []);

  const handleAudioOverlayTap = useCallback(() => {
    warmUpAudio();
    setShowAudioOverlay(false);
  }, []);

  useEffect(() => {
    // Try to unlock immediately - may work if user just navigated here from form submit
    initAudio(AlertSound, true);

    // Show overlay if audio is not yet unlocked (needed for iOS/iPadOS)
    setTimeout(() => {
      if (!isAudioUnlocked()) {
        setShowAudioOverlay(true);
      }
    }, 300);

    const handleUserGesture = () => {
      warmUpAudio();
      setShowAudioOverlay(false);
    };

    // Listen for various user interactions to unlock audio on iOS
    document.addEventListener('click', handleUserGesture, { once: true });
    document.addEventListener('touchstart', handleUserGesture, { once: true });
    document.addEventListener('touchend', handleUserGesture, { once: true });
    document.addEventListener('scroll', handleUserGesture, {
      once: true,
      passive: true,
    });
    document.addEventListener('keydown', handleUserGesture, { once: true });
    window.addEventListener('focus', handleUserGesture, { once: true });

    return () => {
      document.removeEventListener('click', handleUserGesture);
      document.removeEventListener('touchstart', handleUserGesture);
      document.removeEventListener('touchend', handleUserGesture);
      document.removeEventListener('scroll', handleUserGesture);
      document.removeEventListener('keydown', handleUserGesture);
      window.removeEventListener('focus', handleUserGesture);
    };
  }, []);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    globalThis.addEventListener('online', handleOnline);
    globalThis.addEventListener('offline', handleOffline);

    return () => {
      globalThis.removeEventListener('online', handleOnline);
      globalThis.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (consultId && isWaitingStatus) {
        const navEntry = performance.getEntriesByType(
          'navigation'
        )[0] as PerformanceNavigationTiming;
        const isRefresh = navEntry?.type === 'reload';

        if (!isRefresh && user?.userId) {
          fetch(
            `${import.meta.env.VITE_INTEGRATION_BASE_URL}/integration/consult/${consultId}/heartbeat`,
            {
              method: 'DELETE',
              keepalive: true,
              headers: {
                'Content-Type': 'application/json',
                portalType: PORTAL_TYPES.FACILITY,
                userId: user.userId.toString(),
              },
            }
          ).catch((error) => {
            console.error('Failed to delete heartbeat on unload:', error);
          });
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);

      const navigationType = globalThis.performance?.getEntriesByType?.(
        'navigation'
      )?.[0] as PerformanceNavigationTiming;
      const isPageRefresh = navigationType?.type === 'reload';

      if (!isPageRefresh) {
        deleteHeartbeat();
      }
    };
  }, [consultId, isWaitingStatus]);

  useEffect(() => {
    if (isStatusLoading || !consultStatus || !currentStatus) return;

    if (consultStatus?.isErrorPage) {
      deleteHeartbeat().then(() => {
        navigate(RE_SUBMIT_OR_CANCEL_CONSULT_ROUTE_PATH, {
          state: {
            consultId: consultId,
            error: consultStatus.errorType || 'abandoned',
          },
          replace: true,
        });
      });
      return;
    }

    const status = currentStatus.toLowerCase();

    const isCallStarted = status === CallStatusEnum.CallStarted.toLowerCase();
    const isCompleted = status === CallStatusEnum.CallCompleted.toLowerCase();
    const isCanceled = status === CallStatusEnum.Cancelled.toLowerCase();
    const isWaiting = consultStatus?.isWaitingRoom;
    const isFacilityChanged = consultStatus?.isFacilityChanged;

    const getDashboardPath = () => {
      return user?.role?.toString() === ROLES.CASE_MANAGER
        ? CASEMANGER_CONSULT_REQUEST_DASHBOARD_ROUTE_PATH
        : FACILITY_CONSULT_REQUEST_DASHBOARD_ROUTE_PATH;
    };

    const navigateToDashboard = (options = {}) => {
      navigate(getDashboardPath(), { replace: true, ...options });
    };

    let timer: ReturnType<typeof setTimeout> | null = null;

    if (isCallStarted) {
      if (isWaiting) {
        setOpenConfirmationPopup(true);
      } else {
        setOpenConfirmationPopup(false);
        stopAlert();
        deleteHeartbeat()
          .then(() => {
            navigateToDashboard();
          })
          .catch((error) => {
            console.error('Failed to delete heartbeat:', error);
            navigateToDashboard();
          });
      }
    } else if (isCompleted) {
      setShowCallCompletedToast(true);
      setOpenConfirmationPopup(false);
      stopAlert();

      timer = setTimeout(async () => {
        await deleteHeartbeat();
        navigateToDashboard({
          state: { message: 'Call completed successfully' },
        });
      }, 3000);
    } else if (isCanceled || !isWaiting || isFacilityChanged) {
      setOpenConfirmationPopup(false);
      stopAlert();
      deleteHeartbeat().then(() => {
        navigateToDashboard();
      });
    } else {
      setOpenConfirmationPopup(false);
      stopAlert();
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [
    currentStatus,
    consultStatus?.isWaitingRoom,
    consultStatus?.isFacilityChanged,
    isStatusLoading,
  ]);

  const handleCancelRequest = () => {
    setOpenCancelRequest(!openCancelRequest);
  };

  const deleteHeartbeat = async () => {
    if (consultId && user?.userId) {
      try {
        await DeleteHeartBeatAPI(
          Number(consultId),
          PORTAL_TYPES.FACILITY,
          user.userId
        );
      } catch (error) {
        console.error('Failed to delete heartbeat:', error);
      }
    }
  };

  const handleChatToggle = () => {
    setIsChatOpen((prev) => !prev);
  };

  const handleOnHoldConsult = () => {
    setOpenOnHoldConsult(true);
  };

  const handleSubmitOnHoldReason = (data: OnHoldReasonFacilityParams) => {
    setOnHoldConsultValues(data);
    saveOnHoldConsult(
      {
        userId: user?.userId.toString() ?? '',
        portalType: PORTAL_TYPES.FACILITY,
        consultId: Number(consultId),
        ...data,
      },
      {
        onSuccess: () => {
          putConsultOnHold(
            {
              consultId: Number(consultId),
              userId: user?.userId ?? 0,
            },
            {
              onSuccess: async () => {
                await deleteHeartbeat();
                setOpenOnHoldConsult(false);
                navigate(`${CASEMANGER_CONSULT_REQUEST_DASHBOARD_ROUTE_PATH}`, {
                  replace: true,
                });
              },
            }
          );
        },
      }
    );
  };

  const displayName =
    `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || 'FacilityUser';

  const handleSubmitCancelReason = (data: CancelReasonFacilityParams) => {
    const mappedTechnicalDifficultiesId =
      [
        data.patientAdmittedId,
        data.noLongerRequiredId,
        data.patientDischargedId,
        data.technicalDifficultiesId,
      ].find((id) => id !== 0) ?? 0;

    const payload: CancelReasonFacilitySubmitParams = {
      cancelReasonId: data.cancelReasonId,
      technicalDifficultiesId: mappedTechnicalDifficultiesId,
      otherReason: data.otherReason,
    };
    cancelConsult(
      {
        userId: user?.userId.toString() ?? '',
        portalType: PORTAL_TYPES.FACILITY,
        consultId: Number(consultId),
        ...payload,
      },
      {
        onSuccess: async () => {
          await deleteHeartbeat();
          const dashboardPath =
            user?.role?.toString() === ROLES.CASE_MANAGER
              ? CASEMANGER_CONSULT_REQUEST_DASHBOARD_ROUTE_PATH
              : FACILITY_CONSULT_REQUEST_DASHBOARD_ROUTE_PATH;
          navigate(dashboardPath, {
            replace: true,
            state: { message: 'The consult request has been canceled.' },
          });

          setOpenCancelRequest(false);
        },
      }
    );
  };

  return (
    <>
      {showAudioOverlay && (
        <Box
          onClick={handleAudioOverlayTap}
          onTouchEnd={handleAudioOverlayTap}
          className="audio-overlay"
        />
      )}

      {showCallCompletedToast && (
        <CustomSnackbar
          id="call-completed-toast"
          successOrError={'success'}
          open={showCallCompletedToast}
          handleClose={() => setShowCallCompletedToast(false)}
          toastrMessage={'Call completed successfully'}
        />
      )}
      {isLoading && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <CustomTypography
            id="loading-text"
            variant="h5"
            className="text-align-center pt-32"
            color="textDisabled"
          >
            Loading...
          </CustomTypography>
        </Box>
      )}
      <Box className="waiting-page-container">
        <Grid2
          container
          spacing={3}
          className="width-100 waiting-grid-container"
        >
          <Grid2
            size={{
              xs: 12,
              sm: isChatOpen ? 8.5 : 12,
              md: isChatOpen ? 8.5 : 12,
              lg: isChatOpen ? 8.5 : 12,
              xl: isChatOpen ? 8.5 : 12,
            }}
            className="waiting-content-column"
          >
            {waitingRoomData && (
              <>
                <Box>
                  <ConfirmationPopup
                    openPopup={openConfirmationPopup}
                    onClosePopup={() => {
                      setOpenConfirmationPopup(false);
                    }}
                    title={'Video Call Alert'}
                    message={
                      'Doctor has already joined the call. Please click on below button to join the call.'
                    }
                    onConfirm={async () => {
                      setOpenConfirmationPopup(false);
                      setHasJoinedCall(true);

                      if (consultId && user?.userId) {
                        try {
                          await DeleteHeartBeatAPI(
                            Number(consultId),
                            PORTAL_TYPES.FACILITY,
                            user.userId
                          );
                        } catch (error) {
                          console.error(
                            'Failed to delete heartbeat on join:',
                            error
                          );
                        }
                      }

                      stopAlert();
                      // Join as participant (role=0) via internal ZoomCall component
                      // This enables the chat button for facility users
                      startZoomMeeting(
                        0,
                        Number(consultId),
                        displayName,
                        user?.role?.toString()
                      );
                    }}
                    isAudioRequired={true}
                  />
                </Box>

                <Box>
                  <Grid2 container>
                    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                      <Box className="text-align-right">
                        <Box className="display-session-id">
                          <SessionIcon />
                          <CustomTypography id="session-id-text" variant="h6">
                            Session ID -{' '}
                            <span className="consultid">
                              {waitingRoomData?.sessionId}
                            </span>
                          </CustomTypography>
                        </Box>
                      </Box>
                    </Grid2>
                  </Grid2>
                </Box>
                {currentStatus?.toLowerCase() === CallStatusEnum.InProgress && (
                  <Box
                    display="flex"
                    justifyContent="center"
                    className="inprogress-wrapper-parent"
                  >
                    <Box className="inprogress-wrapper">
                      <MonitorIcon />
                      <CustomTypography
                        id="inprogress-text"
                        variant="h2"
                        className="inprogress-text-color"
                      >
                        The doctor has accepted the consultation and will be
                        joining shortly
                      </CustomTypography>
                    </Box>
                  </Box>
                )}
                {!isOnline && (
                  <Box
                    display="flex"
                    justifyContent="center"
                    className="inprogress-wrapper-parent"
                  >
                    <Box className="offline-warning-wrapper">
                      <CustomTypography
                        id="offline-warning-text"
                        variant="h2"
                        className="offline-warning-text"
                      >
                        Connection Lost. Check your network connection and try
                        again.
                      </CustomTypography>
                    </Box>
                  </Box>
                )}
                <Box>
                  <Grid2 container>
                    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                      <Box className="text-align-center-webkit">
                        <Box className="waiting-background-wrapper">
                          <Box
                            className={`waiting-background ${imageLoaded ? 'loaded' : ''}`}
                          />
                          <Box className="round-container">
                            <Box className="outer-circle" />
                            <Box className="inner-circle">
                              <Box className="waiting-status-display">
                                <CustomChip
                                  id="consult-waiting-status"
                                  color={chipColor as CustomChipColor}
                                  className="chip-text-color status-chip"
                                  label={
                                    callStatusToString[
                                      currentStatus?.toLocaleLowerCase() as CallStatusEnum
                                    ]
                                  }
                                  icon={chipIcon}
                                  iconPosition="left"
                                  size="small"
                                />
                              </Box>
                              <CustomTypography
                                id="waiting-patient-name"
                                variant="h3"
                                color="textPrimary"
                              >
                                {getMaskedOrOriginal(
                                  waitingRoomData?.patientFirstName,
                                  waitingRoomData?.patientLastName,
                                  isPHIMaskingEnabled
                                )}
                              </CustomTypography>
                              <CustomTypography
                                id="waiting-time"
                                color="textPrimary"
                                className="pt-36 waiting-time-font-size"
                              >
                                {waitingRoomData?.status.toLocaleLowerCase() ===
                                  CallStatusEnum.New ||
                                waitingRoomData?.status.toLocaleLowerCase() ===
                                  CallStatusEnum.InProgress ||
                                waitingRoomData?.status.toLocaleLowerCase() ===
                                  CallStatusEnum.OnHold
                                  ? waitingRoomData?.createdOn && (
                                      <TimeTicker
                                        createdOn={waitingRoomData.createdOn}
                                        isWaitingRoom={true}
                                      />
                                    )
                                  : waitingRoomData?.createdOn &&
                                    waitingRoomData?.waitEndTime && (
                                      <TimeDifference
                                        createdOn={waitingRoomData.createdOn}
                                        waitEndTime={
                                          waitingRoomData.waitEndTime
                                        }
                                        isWaitingRoom={true}
                                      />
                                    )}
                              </CustomTypography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                      {currentStatus?.toLowerCase() === CallStatusEnum.New && (
                        <Box className="text-align-center">
                          <CustomTypography
                            id="waiting-info-text"
                            variant="body2"
                            color="textDisabled"
                            className="overlay-text"
                          >
                            Your request has been received. The doctor will be
                            with you shortly.
                          </CustomTypography>
                        </Box>
                      )}
                    </Grid2>
                  </Grid2>
                </Box>
                <Box>
                  <Grid2 container>
                    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                      <Box className="pb-12 flex justify-end">
                        <Box>
                          <CustomBadge
                            id={`${waitingRoomData.consultId}-chat-notification`}
                            badgeContent={waitingRoomData?.unreadMessageCount}
                            color="error"
                            max={99}
                          >
                            <CustomButton
                              id="chat-waiting-page"
                              variant="contained"
                              color="primary"
                              onClick={handleChatToggle}
                            >
                              Chat
                            </CustomButton>
                          </CustomBadge>
                        </Box>
                        {user?.role === ROLES.CASE_MANAGER && (
                          <Box>
                            <CustomButton
                              id="request-on-hold"
                              variant="contained"
                              color="secondary"
                              onClick={handleOnHoldConsult}
                            >
                              Consult to On-Hold
                            </CustomButton>
                          </Box>
                        )}
                        {currentStatus?.toLowerCase() !==
                          CallStatusEnum.CallCompleted &&
                          currentStatus?.toLowerCase() !==
                            CallStatusEnum.Completed && (
                            <Box>
                              <CustomButton
                                id="cancel-request"
                                variant="outlined"
                                color="error"
                                onClick={() => handleCancelRequest()}
                                className="ml-6"
                              >
                                Cancel Request
                              </CustomButton>

                              <Box>
                                {openCancelRequest && (
                                  <CancelConsultModel
                                    openCancelConsultPopup={openCancelRequest}
                                    onCloseCancelConsultPopup={() =>
                                      setOpenCancelRequest(false)
                                    }
                                    ConsultCancelMetaData={
                                      ConsultCancelMetaData
                                    }
                                    onSubmitCancelConsult={(values) => {
                                      handleSubmitCancelReason(values);
                                    }}
                                    portal={PORTAL_TYPES.FACILITY}
                                  />
                                )}
                              </Box>
                            </Box>
                          )}
                        {openOnHoldConsult && (
                          <OnHoldConsultModel
                            openOnHoldConsultPopup={openOnHoldConsult}
                            onCloseOnHoldConsultPopup={() => {
                              setOpenOnHoldConsult(false);
                              if (
                                Object.keys(onHoldConsultValues).length === 0
                              ) {
                                setOnHoldConsultValues(
                                  {} as OnHoldReasonFacilityParams
                                );
                              }
                            }}
                            ConsultOnHoldMetaData={
                              OnHoldConsultMetadata ?? undefined
                            }
                            onSubmitOnHoldConsult={(
                              values: OnHoldReasonFacilityParams
                            ) => {
                              handleSubmitOnHoldReason(values);
                            }}
                            initialValues={onHoldConsultValues}
                          />
                        )}
                      </Box>
                    </Grid2>
                  </Grid2>
                </Box>
              </>
            )}
          </Grid2>
          <Grid2
            size={{
              xs: 12,
              sm: isChatOpen ? 3.5 : 0,
              md: isChatOpen ? 3.5 : 0,
              lg: isChatOpen ? 3.5 : 0,
              xl: isChatOpen ? 3.5 : 0,
            }}
          >
            <Box className="waiting-chat-wrapper">
              <ChatQLER
                onClose={() => setIsChatOpen(false)}
                acsConfig={
                  acsChatToken
                    ? {
                        endpoint: import.meta.env.VITE_ACS_ENDPOINT,
                        token: acsChatToken.token,
                        userId: acsChatToken.acsUserId,
                        displayName:
                          `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() ||
                          'User',
                        portalType: PORTAL_TYPES.FACILITY,
                      }
                    : undefined
                }
                threadId={consultChatThread?.threadId}
                subtitle={consultChatThread?.participants || ''}
                hidden={!isChatOpen}
              />
            </Box>
          </Grid2>
        </Grid2>
      </Box>
    </>
  );
};

export default Waiting;
